/**
 * Bookshelf generator — a freestanding bookcase built from box geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width  (left–right)
 *   - Y = depth  (front–back, shallow)
 *   - Z = height (floor to top)
 *
 * Default dimensions approximate a standard freestanding bookcase:
 *   0.8 m wide × 0.3 m deep × 1.8 m tall, with 5 evenly spaced shelves.
 *
 * Structure:
 *   - Two side panels (full height)
 *   - Top panel and bottom panel (inside the side panels)
 *   - N horizontal shelves distributed between bottom and top
 *   - Optional back panel
 *   - Optional toe kick (recessed bottom base panel)
 */

import type { BoxSpec } from "../generators/generatorGeom.ts";
import { concatBoxes } from "../generators/generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  intParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface BookshelfParams extends GeneratorParams {
  width: number;
  depth: number;
  height: number;
  panelThickness: number;
  shelfCount: number;
  hasToeKick: boolean;
  toeKickHeight: number;
  toeKickDepth: number;
  hasBack: boolean;
  backThickness: number;
}

const BOOKSHELF_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "bookshelf-dimensions",
    label: "Dimensions",
    params: [
      {
        id: "width",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 0.8,
        min: 0.3,
        max: 3,
        step: 0.05,
      },
      {
        id: "depth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.3,
        min: 0.15,
        max: 0.6,
        step: 0.025,
      },
      {
        id: "height",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 1.8,
        min: 0.5,
        max: 3,
        step: 0.05,
      },
      {
        id: "panelThickness",
        label: "Panel Thickness",
        type: "number",
        unit: "meters",
        default: 0.02,
        min: 0.01,
        max: 0.05,
        step: 0.005,
      },
    ],
  },
  {
    type: "section",
    id: "bookshelf-shelves",
    label: "Shelves",
    params: [
      {
        id: "shelfCount",
        label: "Shelf Count",
        type: "number",
        unit: "count",
        default: 5,
        min: 0,
        max: 12,
        step: 1,
      },
    ],
  },
  {
    type: "section",
    id: "bookshelf-back",
    label: "Back & Base",
    defaultCollapsed: true,
    params: [
      { id: "hasBack", label: "Back Panel", type: "boolean", default: true },
      {
        id: "backThickness",
        label: "Back Thickness",
        type: "number",
        unit: "meters",
        default: 0.008,
        min: 0.004,
        max: 0.02,
        step: 0.002,
        showIf: { id: "hasBack", value: true },
      },
      { id: "hasToeKick", label: "Toe Kick", type: "boolean", default: true },
      {
        id: "toeKickHeight",
        label: "Kick Height",
        type: "number",
        unit: "meters",
        default: 0.08,
        min: 0.03,
        max: 0.15,
        step: 0.01,
        showIf: { id: "hasToeKick", value: true },
      },
      {
        id: "toeKickDepth",
        label: "Kick Inset",
        type: "number",
        unit: "meters",
        default: 0.05,
        min: 0.02,
        max: 0.1,
        step: 0.005,
        showIf: { id: "hasToeKick", value: true },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

function buildBookshelf(
  p: BookshelfParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const width = numParam(p, "width", 0.8);
  const depth = numParam(p, "depth", 0.3);
  const height = numParam(p, "height", 1.8);
  const panelT = numParam(p, "panelThickness", 0.02);
  const shelfN = intParam(p, "shelfCount", 5);
  const hasBack = boolParam(p, "hasBack", true);
  const backT = numParam(p, "backThickness", 0.008);
  const hasToe = boolParam(p, "hasToeKick", true);
  const toeH = numParam(p, "toeKickHeight", 0.08);
  const toeInset = numParam(p, "toeKickDepth", 0.05);

  const boxes: BoxSpec[] = [];

  // Back panel depth offset — shelves / sides stop short of the very back
  // so the back panel can sit flush against the wall.
  const backOffset = hasBack ? backT : 0;

  // Inner width and depth (between the side panels)
  const innerW = width - panelT * 2;
  const innerD = depth - backOffset - panelT; // one front-facing panel thickness
  const innerDCY = -backOffset * 0.5; // Y centre of inner cavity

  // Bottom panel Z range — accounts for toe kick recessing
  const bodyBottomZ = hasToe ? toeH : 0;

  // Side panels — full height, cover the outer Y extent of the unit
  const sideCZ = height * 0.5;
  const sideSY = depth - backOffset;
  const sideCY = -backOffset * 0.5;
  boxes.push({
    cx: width * 0.5 - panelT * 0.5,
    cy: sideCY,
    cz: sideCZ,
    sx: panelT,
    sy: sideSY,
    sz: height,
  });
  boxes.push({
    cx: -width * 0.5 + panelT * 0.5,
    cy: sideCY,
    cz: sideCZ,
    sx: panelT,
    sy: sideSY,
    sz: height,
  });

  // Top panel — sits inside the side panels at the very top
  boxes.push({
    cx: 0,
    cy: innerDCY,
    cz: height - panelT * 0.5,
    sx: innerW,
    sy: innerD,
    sz: panelT,
  });

  // Bottom panel — sits inside the side panels at bodyBottomZ
  boxes.push({
    cx: 0,
    cy: innerDCY,
    cz: bodyBottomZ + panelT * 0.5,
    sx: innerW,
    sy: innerD,
    sz: panelT,
  });

  // Toe kick — a narrower panel running the full width at the very bottom.
  // Spans from the back-panel front face to (front face − toeInset), i.e.
  // it is recessed inward from the front by toeInset.
  if (hasToe) {
    const toeD = depth - backOffset - toeInset;
    // Centre Y: back edge is at -(depth/2 - backOffset), front edge is at (depth/2 - toeInset)
    const toeCY = -(depth * 0.5) + backOffset + toeD * 0.5;
    boxes.push({
      cx: 0,
      cy: toeCY,
      cz: toeH * 0.5,
      sx: innerW,
      sy: toeD,
      sz: toeH,
    });
  }

  // Shelves — evenly distributed in the interior space between bottom and top
  // The usable interior height is from (bodyBottomZ + panelT) to (height - panelT).
  const interiorBot = bodyBottomZ + panelT;
  const interiorTop = height - panelT;
  const interiorH = interiorTop - interiorBot;

  if (shelfN > 0) {
    // shelfN shelves are placed at even intervals, not counting top and bottom.
    const gap = interiorH / (shelfN + 1);
    for (let i = 1; i <= shelfN; i++) {
      const shelfZ = interiorBot + gap * i;
      boxes.push({
        cx: 0,
        cy: innerDCY,
        cz: shelfZ,
        sx: innerW,
        sy: innerD,
        sz: panelT,
      });
    }
  }

  // Back panel — thin panel flush with the rear of the unit
  if (hasBack) {
    boxes.push({
      cx: 0,
      cy: -(depth - backT) * 0.5,
      cz: height * 0.5,
      sx: width,
      sy: backT,
      sz: height,
    });
  }

  return { kind: "mesh", geometry: concatBoxes(boxes) };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const BOOKSHELF_GENERATOR: Generator = defineGenerator<BookshelfParams>({
  id: "bookshelf",
  label: "Bookshelf",
  category: "prop",
  paramSpecs: BOOKSHELF_SPECS,
  defaultParams: paramsFromSpecs(BOOKSHELF_SPECS) as BookshelfParams,
  build: buildBookshelf,
});
